// src/pages/ChangePasswordPage.js - 기업 사용자 초기 비밀번호 변경 페이지

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../context/AppContext";
import { enterpriseChangePassword } from "../services/api";
import { useB2BMode } from "../hooks/useB2BMode";
import "./ChangePasswordPage.scss";

const ChangePasswordPage = () => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const navigate = useNavigate();
  const { loginUser, logoutUser } = useAppContext();
  const { enableB2BMode, disableB2BMode } = useB2BMode();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // 유효성 검사
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError("모든 필드를 입력해주세요.");
      return;
    }

    if (newPassword.length < 8) {
      setError("새 비밀번호는 최소 8자 이상이어야 합니다.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("새 비밀번호와 확인 비밀번호가 일치하지 않습니다.");
      return;
    }

    setLoading(true);

    try {
      const data = await enterpriseChangePassword(currentPassword, newPassword);
      if (data.success) {
        setSuccess(true);
        // 새 쿠키가 설정되었으므로 사용자 정보만 업데이트 (JWT는 HttpOnly Cookie)
        if (data.user) {
          loginUser(data.user, null);
        }
        // 1.5초 후 리다이렉트
        setTimeout(() => {
          const isEnterprise =
            data.user?.account_type === "business" &&
            data.user?.tier === "enterprise";
          if (isEnterprise) {
            enableB2BMode();
            navigate("/b2b");
          } else {
            enableB2BMode();
            navigate("/b2b");
          }
        }, 1500);
      } else {
        setError(data.error || "비밀번호 변경에 실패했습니다.");
      }
    } catch (err) {
      console.error("비밀번호 변경 실패:", err);
      setError(err.message || "비밀번호 변경 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    setError("");
    try {
      await logoutUser();
      disableB2BMode();
      navigate("/login", { replace: true });
    } finally {
      setLoggingOut(false);
    }
  };

  return (
    <div className="change-password-page">
      <div className="change-password-container">
        <div className="change-password-card">
          <div className="change-password-header">
            <h1>비밀번호 변경</h1>
            <p>보안을 위해 초기 비밀번호를 변경해주세요.</p>
          </div>

          {success ? (
            <div className="change-password-success">
              <div className="success-icon">✓</div>
              <h2>비밀번호가 변경되었습니다</h2>
              <p>잠시 후 대시보드로 이동합니다.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="change-password-form">
              {error && <div className="error-message">{error}</div>}

              <div className="form-group">
                <label htmlFor="current-password">현재 비밀번호</label>
                <input
                  id="current-password"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="현재 비밀번호를 입력하세요"
                  disabled={loading}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="new-password">새 비밀번호</label>
                <input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="최소 8자 이상"
                  disabled={loading}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirm-password">새 비밀번호 확인</label>
                <input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="새 비밀번호를 다시 입력하세요"
                  disabled={loading}
                  required
                />
              </div>

              <button
                type="submit"
                className="change-password-submit-btn"
                disabled={loading || loggingOut}
              >
                {loading ? "변경 중..." : "비밀번호 변경"}
              </button>

              <button
                type="button"
                className="change-password-logout-btn"
                onClick={handleLogout}
                disabled={loading || loggingOut}
              >
                {loggingOut ? "로그아웃 중..." : "로그아웃"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
